import { API_URL } from "@/constants/api";
import { isProtectedSpot, getProtectedSpotWarning, findProtectedSpot, isProtectedSpotError } from "@/constants/spots";

export interface SpotManagementResult {
  success: boolean;
  message?: string;
  isProtectedSpotError?: boolean;
}

export interface SpotInfo {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  countryid: number;
  countryName?: string;
  continentName?: string;
  alpha2?: string;
  meanreviews?: number;
  nreviews?: number;
  nriders?: number;
  totalsessions?: number;
  maxheight?: number;
  maxairtime?: number;
  maxdistance?: number;
  riderslive?: number;
  maxheightlive?: number;  // Highest live jump at this spot
  wgspotid?: number;  // Windguru forecast station ID
  wgLiveId?: number;  // Legacy Windguru live id (read-only; superseded by liveWind)
  windStationId?: string;  // Live-wind Worker station id
  liveWind?: { stationId: string } | null;  // SRF-2854 assignment block (identifier only)
  timezoneName?: string;  // camelCase as returned by backend
  utcOffset?: number;
  validated?: boolean;
}

export interface NearbySpot {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  distance: number; // in km
  sessionCount?: number;
  reviewCount?: number;
}

export interface MergeSpotRequest {
  id: number;
  userid: number;
  targetspotid: number;
  selectedspotid: number;
  username: string;
  targetspotname: string;
  sourcespotname: string;
  targetLat: number;
  targetLon: number;
  sourceLat: number;
  sourceLon: number;
  targetreviews: number;
  sourcereviews: number;
  targetsessions: number;
  sourcesessions: number;
}

export interface EditSpotRequest {
  spotid: number;
  name: string;
  latitude: number;
  longitude: number;
  wgspotid?: number;
  /** undefined = unchanged, null = clear, string = assign (validated against the Worker first). */
  liveWindStationId?: string | null;
}

// Calculate distance between two coordinates using Haversine formula
export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

export class SpotManagementService {
  private static baseUrl = API_URL;

  /**
   * Get detailed spot information
   */
  static async getSpotInfo(spotId: number, accessToken: string): Promise<SpotInfo | null> {
    try {
      const response = await fetch(
        `${this.baseUrl}/spots/spotinfo/${accessToken}/${spotId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error("Error fetching spot info:", error);
      return null;
    }
  }

  /**
   * Edit spot (name and/or coordinates, Windguru forecast id, live-wind station assignment).
   * `liveWindStationId`: undefined = unchanged, null = clear, string = assign. The backend
   * refuses ids that fail the Worker id gate (400 invalid_station_id).
   */
  static async editSpot(
    accessToken: string,
    spotId: number,
    name: string,
    latitude: number,
    longitude: number,
    wgspotid?: number,
    liveWindStationId?: string | null,
    countryid?: number,
  ): Promise<SpotManagementResult> {
    // Pre-flight check for protected spots
    if (isProtectedSpot(spotId)) {
      return {
        success: false,
        message: getProtectedSpotWarning(spotId),
        isProtectedSpotError: true,
      };
    }

    try {
      // Backend expects query parameters, not JSON body
      const params = new URLSearchParams({
        spotid: spotId.toString(),
        name: name,
        latitude: latitude.toString(),
        longitude: longitude.toString(),
      });
      if (wgspotid !== undefined) {
        params.set("wgspotid", wgspotid.toString());
      }
      if (liveWindStationId !== undefined) {
        // Query params cannot carry null: the backend treats an empty value as "clear".
        params.set("liveWindStationId", liveWindStationId ?? "");
      }
      if (countryid !== undefined) {
        params.set("countryid", countryid.toString());
      }
      const response = await fetch(
        `${this.baseUrl}/spots/editspot/${accessToken}?${params.toString()}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) {
        // Try to parse as JSON for structured error
        try {
          const errorData = await response.json();
          if (isProtectedSpotError(errorData)) {
            return {
              success: false,
              message: errorData.message,
              isProtectedSpotError: true,
            };
          }
          return { success: false, message: errorData.message || "Failed to edit spot" };
        } catch {
          const errorText = await response.text();
          return { success: false, message: errorText || "Failed to edit spot" };
        }
      }
      return { success: true };
    } catch (error) {
      console.error("Error editing spot:", error);
      return { success: false, message: "Network error" };
    }
  }

  /**
   * Get nearby spots for merge suggestions
   */
  static async getNearbySpots(
    latitude: number,
    longitude: number,
    currentSpotId: number,
    radiusKm: number = 10
  ): Promise<NearbySpot[]> {
    try {
      // Use the findspots endpoint with a small delta to get nearby spots
      const delta = radiusKm / 111; // Approximate degrees for the radius
      const response = await fetch(
        `${this.baseUrl}/spots/findspots?lat=${latitude}&lon=${longitude}&latDelta=${delta}&lonDelta=${delta}&maxSpotsShown=50`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) return [];

      const spots = await response.json();

      // Filter out current spot and calculate distances
      return spots
        .filter((s: any) => s.id !== currentSpotId)
        .map((s: any) => ({
          id: s.id,
          name: s.name,
          latitude: s.latitude,
          longitude: s.longitude,
          distance: calculateDistance(latitude, longitude, s.latitude, s.longitude),
          sessionCount: s.totalsessions || 0,
          reviewCount: s.nreviews || 0,
        }))
        .filter((s: NearbySpot) => s.distance <= radiusKm)
        .sort((a: NearbySpot, b: NearbySpot) => a.distance - b.distance);
    } catch (error) {
      console.error("Error fetching nearby spots:", error);
      return [];
    }
  }

  /**
   * Create a merge request (for non-admin users)
   */
  static async createMergeRequest(
    accessToken: string,
    targetSpotId: number,
    sourceSpotId: number
  ): Promise<SpotManagementResult> {
    // Pre-flight check for protected spots (source spot cannot be merged)
    if (isProtectedSpot(sourceSpotId)) {
      return {
        success: false,
        message: getProtectedSpotWarning(sourceSpotId),
        isProtectedSpotError: true,
      };
    }

    try {
      const response = await fetch(
        `${this.baseUrl}/mergespotrequest/addrequest/${accessToken}/${targetSpotId}/${sourceSpotId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) {
        // Try to parse as JSON for structured error
        try {
          const errorData = await response.json();
          if (isProtectedSpotError(errorData)) {
            return {
              success: false,
              message: errorData.message,
              isProtectedSpotError: true,
            };
          }
          return { success: false, message: errorData.message || "Failed to create merge request" };
        } catch {
          const errorText = await response.text();
          return { success: false, message: errorText || "Failed to create merge request" };
        }
      }
      return { success: true };
    } catch (error) {
      console.error("Error creating merge request:", error);
      return { success: false, message: "Network error" };
    }
  }

  /**
   * Execute spot merge (admin only)
   */
  static async mergeSpots(
    accessToken: string,
    targetSpotId: number,
    sourceSpotIds: number[]
  ): Promise<SpotManagementResult> {
    // Pre-flight check for protected spots
    const protectedSpotId = findProtectedSpot(sourceSpotIds);
    if (protectedSpotId !== null) {
      return {
        success: false,
        message: getProtectedSpotWarning(protectedSpotId),
        isProtectedSpotError: true,
      };
    }

    try {
      // Backend expects query parameters with comma-separated IDs
      const params = new URLSearchParams({
        targetspotid: targetSpotId.toString(),
        spotstomergeids: sourceSpotIds.join(","),
      });
      const response = await fetch(
        `${this.baseUrl}/spots/mergespots/${accessToken}?${params.toString()}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) {
        // Try to parse as JSON for structured error
        try {
          const errorData = await response.json();
          if (isProtectedSpotError(errorData)) {
            return {
              success: false,
              message: errorData.message,
              isProtectedSpotError: true,
            };
          }
          return { success: false, message: errorData.message || "Failed to merge spots" };
        } catch {
          const errorText = await response.text();
          return { success: false, message: errorText || "Failed to merge spots" };
        }
      }
      return { success: true };
    } catch (error) {
      console.error("Error merging spots:", error);
      return { success: false, message: "Network error" };
    }
  }

  /**
   * Get pending merge requests (admin only)
   */
  static async getMergeRequests(accessToken: string): Promise<MergeSpotRequest[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/mergespotrequest/list/${accessToken}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) return [];
      return await response.json();
    } catch (error) {
      console.error("Error fetching merge requests:", error);
      return [];
    }
  }

  /**
   * Get spot statistics
   */
  static async getSpotStatistics(spotId: number, accessToken: string): Promise<any> {
    try {
      const response = await fetch(
        `${this.baseUrl}/live/spots/statistics/${spotId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${accessToken}`,
          },
        }
      );
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error("Error fetching spot statistics:", error);
      return null;
    }
  }

  /**
   * Search countries by name.
   */
  static async searchCountries(query: string): Promise<Array<{ id: number; name: string }>> {
    try {
      const response = await fetch(
        `${this.baseUrl}/countries/list/0?searchval=${encodeURIComponent(query)}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) return [];
      return await response.json();
    } catch (error) {
      console.error("Error searching countries:", error);
      return [];
    }
  }

  /**
   * Create a new spot directly (admin only, no approval queue).
   * Returns the created spot ID on success.
   */
  static async createSpot(
    accessToken: string,
    name: string,
    latitude: number,
    longitude: number,
  ): Promise<{ success: boolean; spotId?: number; message?: string }> {
    try {
      const response = await fetch(
        `${this.baseUrl}/spots?accesstoken=${accessToken}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, latitude, longitude }),
        }
      );
      if (response.status === 409) {
        return { success: false, message: "A spot with this name already exists" };
      }
      if (!response.ok) {
        const errorText = await response.text();
        return { success: false, message: errorText || "Failed to create spot" };
      }
      return { success: true };
    } catch (error) {
      console.error("Error creating spot:", error);
      return { success: false, message: "Network error" };
    }
  }

  /**
   * Detect country from coordinates (reverse geocode).
   */
  static async getCountryByLocation(
    latitude: number,
    longitude: number,
    accessToken: string,
  ): Promise<{ id: number; name: string } | null> {
    try {
      const response = await fetch(
        `${this.baseUrl}/countries/bylocation?lat=${latitude}&lon=${longitude}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${accessToken}`,
          },
        }
      );
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error("Error detecting country:", error);
      return null;
    }
  }
}
